import _, { keyBy } from 'lodash'

import { getQueryParamId, setQueryParam } from '@app/utils/url-helpers'

import * as mutationTypes from './mutation-types'
import api from '../api/index'
import { SORT_DEFAULTS, sortCompanyIds } from '../constants/sort'

import * as communicationMutationTypes from '@app/application/communication/sections/communication/store/mutation-types'
import { REMARK_ACTIONS } from '@app/application/communication/constants'
import { abort } from '@app/adapters/abort'

function isCompanyAlreadyActive(state, id) {
  return (
    state.notifications.initialDataFetched &&
    id === state.notifications.activeCompanyId
  )
}

export default {
  state() {
    return {
      notifications: {},
      initialUnreadStateById: {},
      companies: {},
      unreadOrder: [],
      readOrder: [],
      unreadSort: SORT_DEFAULTS.unread,
      readSort: SORT_DEFAULTS.read,
      activeNotificationId: getQueryParamId('activeNotificationId'),
      activeCompanyId: getQueryParamId('activeCompanyId'),
      search: '',
      initialDataFetched: false,
    }
  },

  getters: {
    notifications: ({ notifications }) =>
      _.sortBy(Object.values(notifications), 'sortIdx'),
    unreadNotificationCount: ({ notifications }) =>
      Object.values(notifications).filter((n) => !n.read).length,
    unreadCompanies: ({ companies, unreadOrder }) =>
      unreadOrder
        .map((id) => companies[id])
        .filter((c) => c.unreadNotifications > 0),
    readCompanies: ({ companies, readOrder }) =>
      readOrder
        .map((id) => companies[id])
        .filter((c) => c.unreadNotifications === 0),
    activeNotificationId: ({ activeNotificationId }) => activeNotificationId,
    activeCompanyId: ({ activeCompanyId }) => activeCompanyId,
    notificationCommunicationItem:
      ({ notifications }) =>
      (notificationId) => {
        const notification = notifications[notificationId]
        return { id: notification.remarkId, type: 'Remark' }
      },
    querySearch: ({ search }) => (search === '' ? null : search),
    notificationsByRemarkId: ({ notifications }) => {
      return keyBy(
        Object.values(notifications).filter((n) => n.remarkId),
        'remarkId'
      )
    },
  },
  mutations: {
    [mutationTypes.SET_NOTIFICATION](state, notifications) {
      state.notifications = notifications
      for (const notification of Object.values(notifications)) {
        state.initialUnreadStateById[notification.id] ||= !notification.read
      }
    },
    [mutationTypes.RESET_ACTIVE_NOTIFICATION](state) {
      state.activeNotificationId = null

      setQueryParam('activeNotificationId', undefined)
    },
    [mutationTypes.RESET_NOTIFICATIONS](state) {
      state.notifications = {}
    },
    [mutationTypes.SET_COMPANIES](state, companies) {
      state.companies = companies
    },
    [mutationTypes.SET_INBOX_COMPANY_ORDER](state, { unread, read }) {
      state.unreadOrder = unread
      state.readOrder = read
    },
    [mutationTypes.SET_INBOX_SORT](state, { section, value }) {
      if (section === 'unread') {
        state.unreadSort = value
      } else if (section === 'read') {
        state.readSort = value
      }
    },
    [mutationTypes.SET_ACTIVE_NOTIFICATION](state, id) {
      state.activeNotificationId = id

      setQueryParam('activeNotificationId', id)
    },
    [mutationTypes.SET_ACTIVE_COMPANY](state, { id, replace }) {
      state.activeCompanyId = id
      state.initialUnreadStateById = {}

      setQueryParam('activeCompanyId', id, {
        replace,
      })
    },
    [mutationTypes.RESET_ACTIVE_COMPANY](state) {
      state.activeCompanyId = null

      setQueryParam('activeCompanyId', undefined)
    },
    [mutationTypes.SET_NOTIFICATION_READ_STATE](state, { id, read }) {
      state.notifications[id].read = read
      state.initialUnreadStateById[id] ||= !read
    },
    [mutationTypes.SET_NOTIFICATION_LINKED_COMMENT_ID](
      state,
      { id, linkedCommentId }
    ) {
      state.notifications[id].linkedCommentId = linkedCommentId
    },
    [mutationTypes.SET_COMPANY_NOTIFICATION_COUNT](state, { id, count }) {
      state.companies[id].unreadNotifications = count
    },
    [mutationTypes.SET_SEARCH](state, search) {
      state.search = search
    },
    [mutationTypes.RESET_NOTIFICATION_READ_CACHE](state) {
      for (const notification of Object.values(state.notifications)) {
        state.initialUnreadStateById[notification.id] = !notification.read
      }
    },
    [mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID](state, id) {
      state.initialUnreadStateById[id] = !state.notifications[id].read
    },
  },
  actions: {
    fetchNotifications(
      { commit, state, getters, dispatch, rootState },
      companyId
    ) {
      commit(mutationTypes.SET_LOADING, { type: 'notifications', value: true })

      return new Promise((resolve) => {
        api
          .getNotifications({
            companyId:
              companyId || state.activeCompanyId || Silverfin.currentCompanyId,
            ledgerId: rootState.ledgerId,
            search: getters.querySearch,
          })
          .then((data) => {
            const notifications = { ..._.keyBy(data, 'id') }

            commit(mutationTypes.SET_NOTIFICATION, notifications)
            commit(mutationTypes.SET_LOADING, {
              type: 'notifications',
              value: false,
            })

            //it handles fetching communicationItem and marking it as read if ID was passed through the URL query params
            if (state.activeNotificationId) {
              const communicationItem = getters.notificationCommunicationItem(
                state.activeNotificationId
              )

              dispatch('fetchCommunicationItem', communicationItem)

              if (!state.notifications[state.activeNotificationId].read) {
                dispatch(
                  'toggleReadNotification',
                  state.activeNotificationId
                ).then(() => {
                  window.eventHub.$emit('resetNotificationCount')
                })
              }
            }

            resolve()
          })
      })
    },

    fetchCompanies(
      { commit, dispatch, state, getters },
      { silent = false, ...filters } = {}
    ) {
      if (!silent) {
        commit(mutationTypes.SET_LOADING, { type: 'companies', value: true })
      }

      filters.search = filters.search || getters.querySearch

      return new Promise((resolve) => {
        api
          .getCompanies({
            filters,
            unreadSort: state.unreadSort,
            readSort: state.readSort,
          })
          .then(({ unread = [], read = [] }) => {
            const all = [...unread, ...read]
            const activeCompanyId =
              state.activeCompanyId || (all.length > 0 ? all[0].id : null)
            const companies = { ..._.keyBy(all, 'id') }

            if (activeCompanyId !== null) {
              dispatch('setActiveCompany', activeCompanyId)
            }
            commit(mutationTypes.SET_COMPANIES, companies)
            commit(mutationTypes.SET_INBOX_COMPANY_ORDER, {
              unread: unread.map((c) => c.id),
              read: read.map((c) => c.id),
            })
            if (!silent) {
              commit(mutationTypes.SET_LOADING, {
                type: 'companies',
                value: false,
              })
            }

            resolve()
          })
      })
    },
    setInboxSort({ commit, dispatch, state }, { section, value }) {
      commit(mutationTypes.SET_INBOX_SORT, { section, value })

      const ids = section === 'unread' ? state.unreadOrder : state.readOrder
      const sortedIds = sortCompanyIds(ids, state.companies, value)
      commit(mutationTypes.SET_INBOX_COMPANY_ORDER, {
        unread: section === 'unread' ? sortedIds : state.unreadOrder,
        read: section === 'read' ? sortedIds : state.readOrder,
      })

      api.updateFirmInboxSort({ section, value })
      return dispatch('fetchCompanies', { silent: true })
    },
    setActiveCompany({ commit, dispatch }, id) {
      if (isCompanyAlreadyActive(this.state, id)) {
        return
      }

      this.state.notifications.initialDataFetched = true

      commit(mutationTypes.SET_ACTIVE_COMPANY, { id, replace: true })
      commit(communicationMutationTypes.RESET_MENTION_USERNAMES)
      commit(mutationTypes.RESET_NOTIFICATIONS)

      abort('communications')
      dispatch('resetCommunicationData')
      dispatch('fetchCommunication')
      dispatch('fetchWorkflows')
      dispatch('fetchNotifications', id)
      dispatch('fetchMentionUsernames')
    },
    handleNotificationClick({ commit, state, dispatch, getters }, id) {
      commit(mutationTypes.SET_ACTIVE_NOTIFICATION, id)

      const communicationItem = getters.notificationCommunicationItem(id)

      dispatch('fetchCommunicationItem', communicationItem)
      if (!state.notifications[id].read) {
        dispatch('toggleReadNotification', id).then(() => {
          window.eventHub.$emit('resetNotificationCount')
        })
      }
    },
    handleFirmNotificationClick({ commit, dispatch }, id) {
      commit(mutationTypes.SET_ACTIVE_COMPANY, { id })
      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
      commit(mutationTypes.RESET_COMMUNICATION_ITEM)
      commit(communicationMutationTypes.RESET_MENTION_USERNAMES)

      dispatch('fetchNotifications', id)
      dispatch('fetchMentionUsernames')
    },
    handleNotificationUnread({ state, dispatch }, id) {
      if (state.notifications[id].read) {
        dispatch('toggleReadNotification', id).then(() => {
          window.eventHub.$emit('resetNotificationCount')
        })
      }
    },
    resetActiveNotification({ commit }) {
      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
    },
    refreshNotificationData({ commit, dispatch, state, getters }, data) {
      if (data.action === REMARK_ACTIONS.EDITED) {
        const { activeNotificationId } = state
        if (activeNotificationId) {
          const communicationItem =
            getters.notificationCommunicationItem(activeNotificationId)

          dispatch('fetchCommunicationItem', communicationItem)
        }
      } else if (data.action === REMARK_ACTIONS.DESTROYED) {
        commit(mutationTypes.SET_ACTIVE_NOTIFICATION, null)
        commit(mutationTypes.SET_COMMUNICATION_ITEM, { remark: null })

        if (state.activeCompanyId) {
          dispatch('fetchNotifications', state.activeCompanyId)
        } else {
          dispatch('fetchNotifications', Silverfin.currentCompanyId)
        }
      } else if (data.action === REMARK_ACTIONS.CREATED) {
        if (state.activeCompanyId) {
          dispatch('fetchNotifications', state.activeCompanyId)
        } else {
          dispatch('fetchNotifications', Silverfin.currentCompanyId)
        }
      }
    },
    toggleReadNotification({ commit, dispatch }, id) {
      return api.toggleReadNotification({ id }).then((response) => {
        commit(mutationTypes.SET_NOTIFICATION_READ_STATE, {
          id: id,
          read: response.read,
        })
        commit(mutationTypes.SET_NOTIFICATION_LINKED_COMMENT_ID, {
          id: id,
          linkedCommentId: response.linkedCommentId,
        })
        commit(mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID, id)

        dispatch('resetCompanyNotificationCount')
      })
    },
    resetCompanyNotificationCount({ commit, dispatch, state, getters }) {
      const companyId = state.activeCompanyId
      if (!_.isEmpty(state.companies)) {
        const previousCount =
          state.companies[companyId]?.unreadNotifications ?? 0
        return new Promise((resolve) => {
          api
            .fetchCompanyUnreadCount({
              companyId,
              search: getters.querySearch,
            })
            .then((response) => {
              commit(mutationTypes.SET_COMPANY_NOTIFICATION_COUNT, {
                id: companyId,
                count: response.count,
              })

              if (previousCount > 0 !== response.count > 0) {
                dispatch('fetchCompanies', { silent: true })
              }

              resolve()
            })
        })
      }
    },
    resetNotificationReadCache({ commit }) {
      commit(mutationTypes.RESET_NOTIFICATION_READ_CACHE)
    },
    handleFirmSearch({ commit, dispatch, state }, value) {
      if (state.search === value) return

      commit(mutationTypes.SET_SEARCH, value)

      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
      commit(mutationTypes.RESET_ACTIVE_COMPANY)
      commit(mutationTypes.RESET_COMMUNICATION_ITEM)

      dispatch('fetchCompanies')
    },
    handleCompanySearch({ commit, dispatch, state }, value) {
      if (state.search === value) return

      commit(mutationTypes.SET_SEARCH, value)

      commit(mutationTypes.RESET_ACTIVE_NOTIFICATION)
      commit(mutationTypes.RESET_COMMUNICATION_ITEM)

      dispatch('fetchNotifications')
    },
  },
}
